import { Link } from "react-router-dom";
import { HiArrowRight, HiOutlineArrowTrendingUp, HiOutlineCheckCircle, HiOutlineCodeBracket, HiOutlineCommandLine, HiOutlineGlobeAlt, HiOutlineSparkles } from "../../components/common/LightIcons";
import Reveal from "../../components/common/Reveal";
import CTA from "../../components/common/CTA";
import Seo from "../../components/common/Seo";
import { blogPosts, projects, services } from "../../data/siteData";

const tech = ["React", "React Native", "TypeScript", "Node.js", "GraphQL", "AWS", "Docker", "Firebase", "Figma", "CI/CD"];

export default function Home() {
  return (
    <>
      <Seo title="Software Development Company" description="Navastha Infotech builds modern web apps, mobile products, cloud solutions, AI automation and dedicated engineering teams from Jaipur, India." />
      <section className="home-hero">
        <div className="hero-grid" />
        <div className="orb orb-one" />
        <div className="orb orb-two" />
        <div className="shell hero-inner">
          <div className="hero-copy hero-enter">
            <span className="eyebrow light"><i /> Digital product engineering</span>
            <h1>We build software that moves <span className="gradient-text">business forward.</span></h1>
            <p>Strategy, design and engineering for web, mobile, cloud and AI-powered products—delivered by a team that works like part of yours.</p>
            <div className="hero-actions"><Link to="/contact" className="btn btn-primary btn-lg">Start your project <span>↗</span></Link><Link to="/portfolio" className="btn btn-ghost btn-lg">See our work <HiArrowRight/></Link></div>
            <div className="hero-proof"><span><HiOutlineCheckCircle/> Product-first thinking</span><span><HiOutlineCheckCircle/> Senior engineering</span><span><HiOutlineCheckCircle/> Flexible engagement</span></div>
          </div>

          <div className="hero-visual hero-enter hero-enter-delay">
            <div className="visual-window glass-card">
              <div className="window-head"><div className="window-dots"><i/><i/><i/></div><span>navastha://build</span><div className="live-pill"><b/> Live</div></div>
              <div className="visual-main">
                <div className="code-panel">
                  <div className="code-label"><HiOutlineCommandLine/> Engineering workspace</div>
                  <div className="code-lines"><span className="w70"/><span className="w92"/><span className="w54"/><span className="w82"/><span className="w64"/><span className="w88"/></div>
                  <div className="metric-row"><div><strong>99.9%</strong><span>reliable delivery</span></div><div><strong>∞</strong><span>room to scale</span></div></div>
                </div>
                <div className="signal-panel">
                  <div className="signal-card signal-blue"><HiOutlineCodeBracket/><div><small>Build</small><strong>Modern products</strong></div><span>01</span></div>
                  <div className="signal-card signal-cyan"><HiOutlineGlobeAlt/><div><small>Scale</small><strong>Cloud ready</strong></div><span>02</span></div>
                  <div className="signal-card signal-violet"><HiOutlineSparkles/><div><small>Transform</small><strong>Applied AI</strong></div><span>03</span></div>
                </div>
              </div>
            </div>
            <div className="floating-chip chip-one"><HiOutlineArrowTrendingUp/><span><small>Delivery velocity</small><b>Built to accelerate</b></span></div>
            <div className="floating-chip chip-two"><span className="pulse-dot"/><span><small>Team status</small><b>Connected & shipping</b></span></div>
          </div>
        </div>
        <div className="hero-bottom-fade" />
      </section>

      <section className="trust-strip">
        <div className="shell trust-inner"><span>Trusted product experience across</span><div className="trust-tags"><b>Commerce</b><b>Fintech</b><b>Analytics</b><b>Mobile</b><b>SaaS</b><b>Sustainability</b></div></div>
      </section>

      <section className="section-pad services-home">
        <div className="shell">
          <Reveal className="section-heading split-heading"><div><span className="eyebrow"><i/> What we do</span><h2>From idea to production, <span>we cover the full product journey.</span></h2></div><p>Choose a focused delivery team or combine capabilities into one integrated product partnership.</p></Reveal>
          <div className="service-grid">
            {services.map((service, index) => { const Icon = service.icon; return (
              <Reveal key={service.slug} delay={(index % 3) * .07}>
                <Link to={`/services/${service.slug}`} className="service-card">
                  <div className="service-card-top"><span className="service-icon"><Icon/></span><span className="card-index">0{index + 1}</span></div>
                  <h3>{service.title}</h3><p>{service.short}</p><span className="card-link">Explore service <HiArrowRight/></span>
                </Link>
              </Reveal>
            );})}
          </div>
        </div>
      </section>

      <section className="section-pad dark-section process-section">
        <div className="process-glow" />
        <div className="shell">
          <Reveal className="section-heading centered"><span className="eyebrow light"><i/> How we work</span><h2>Less handoff. More <span className="gradient-text">momentum.</span></h2><p>Our process keeps product, design and engineering connected from first conversation to continuous delivery.</p></Reveal>
          <div className="process-grid">
            {[
              ["01", "Discover", "We align on users, business goals, constraints and the fastest path to meaningful value."],
              ["02", "Design", "We turn complexity into clear flows, prototypes and a reusable product experience."],
              ["03", "Build", "Cross-functional engineers ship in visible increments with quality built into each release."],
              ["04", "Evolve", "We measure, learn and keep improving performance, features and platform capability."],
            ].map((item, i) => <Reveal key={item[0]} delay={i*.08}><div className="process-card"><span>{item[0]}</span><h3>{item[1]}</h3><p>{item[2]}</p><div className="process-line"/></div></Reveal>)}
          </div>
        </div>
      </section>

      <section className="section-pad work-preview">
        <div className="shell">
          <Reveal className="section-heading split-heading"><div><span className="eyebrow"><i/> Selected work</span><h2>Products built for <span>real-world use.</span></h2></div><Link to="/portfolio" className="text-link">View all projects <HiArrowRight/></Link></Reveal>
          <div className="projects-grid">
            {projects.slice(0, 3).map((project, index) => <Reveal key={project.title} delay={index*.08}><article className="project-card"><div className="project-image"><img src={project.image} alt={`${project.title} project`} loading="lazy" decoding="async"/><div className="project-overlay"/></div><div className="project-meta"><span>{project.type}</span><h3>{project.title}</h3><p>{project.summary}</p><div className="stack-row">{project.stack.map(t => <b key={t}>{t}</b>)}</div></div></article></Reveal>)}
          </div>
        </div>
      </section>

      <section className="tech-marquee-section">
        <div className="marquee-track">{tech.map((item, i) => <span key={`${item}-${i}`}><i/> {item}</span>)}</div>
      </section>

      <section className="section-pad insights-preview">
        <div className="shell">
          <Reveal className="section-heading split-heading"><div><span className="eyebrow"><i/> Insights</span><h2>Thinking for teams that <span>build digital products.</span></h2></div><Link to="/blogs" className="text-link">Explore all insights <HiArrowRight/></Link></Reveal>
          <div className="blog-grid">
            {blogPosts.slice(0, 3).map((post, index) => <Reveal key={post.slug} delay={index*.08}><Link to={`/blogs/${post.slug}`} className="blog-card"><div className={`blog-visual ${post.heroClass}`}><span>{post.category}</span><div className="blog-orbit"><i/><i/><i/></div></div><div className="blog-card-copy"><div className="blog-meta"><span>{post.date}</span><span>{post.readTime}</span></div><h3>{post.title}</h3><p>{post.excerpt}</p><span className="card-link">Read article <HiArrowRight/></span></div></Link></Reveal>)}
          </div>
        </div>
      </section>
      <CTA />
    </>
  );
}
